import React, { useEffect, useRef, useState } from 'react';
import { apiPost } from '../utils/backendApi';
import { Chip } from './ModuleFrame';

// Ask the org's knowledge through the guardrailed endpoint
// (POST /knowledge/:orgId/ask → shared/retrieval.js ask()). One component
// for every surface that needs it: App Builder's live preview, Knowledge's
// "Test guardrails", NoVA Experience's center pane. Shows the answer, its
// citations, and which guardrails fired — the deck's "answer context".
//
// agentToken mode: talk to a deployed agent exactly as a visitor would
// (POST /agent/session + /agent/message — the public embed path), so the
// preview IS the embed: same scope, public audience, same tools.
// Plain words for the guardrail keys the backend reports as fired.
const GUARDRAIL_SHORT = {
  approvedSourcesOnly: 'published documents only',
  citationsRequired: 'needs a source',
  sensitiveDataMasking: 'personal data masked',
  uncertainToReview: 'uncertain → Human Review',
};

export default function AskPane({
  orgId,
  appId = null,
  agentToken = null,
  guardrails = null,     // override map for "test this policy set"
  placeholder = 'Ask a question your users would ask…',
  onHandoff = null,      // (question, answer) => void — renders the human button
  compact = false,
  history = true,
}) {
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [turns, setTurns] = useState([]); // [{ q, out, error }]
  const session = useRef(null);
  useEffect(() => { session.current = null; setTurns([]); }, [agentToken]);

  const askAgent = async (question) => {
    if (!session.current) {
      const s = await apiPost('/agent/session', { token: agentToken });
      if (s.error) throw new Error(s.error);
      session.current = s.sessionId;
    }
    const r = await apiPost('/agent/message', { sessionId: session.current, message: question });
    if (r.error) throw new Error(r.error);
    return { text: r.text, citations: [], guardrails: [], proposals: r.proposals || [] };
  };

  const send = async (e) => {
    e?.preventDefault?.();
    const question = q.trim();
    if (!question || busy) return;
    setBusy(true);
    setQ('');
    try {
      const out = agentToken
        ? await askAgent(question)
        : await apiPost(`/knowledge/${orgId}/ask`, { question, appId, ...(guardrails ? { guardrails } : {}) });
      setTurns((t) => [{ q: question, out }, ...(history ? t : [])]);
    } catch (err) {
      setTurns((t) => [{ q: question, error: err.message }, ...(history ? t : [])]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ask">
      <form onSubmit={send} style={{ display: 'flex', gap: 8 }}>
        <input className="cs-input" style={{ flex: 1 }} value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} disabled={busy} aria-label="Question" />
        <button type="submit" className="cs-btn cs-btn-primary cs-btn-sm" disabled={busy || !q.trim()}>{busy ? 'Asking…' : 'Ask'}</button>
      </form>
      <div style={{ display: 'grid', gap: 10, marginTop: turns.length ? 12 : 0 }}>
        {turns.map((t, i) => (
          <div key={`${t.q}-${i}`} className="mf-row" style={{ alignItems: 'flex-start', flexDirection: 'column', gap: 6 }}>
            <div style={{ fontWeight: 600 }}>Q · {t.q}</div>
            {t.error ? (
              <div className="cs-form-error" role="alert">{t.error}</div>
            ) : (
              <>
                <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{t.out.text}</div>
                {!compact && agentToken && (t.out.proposals || []).length > 0 && (
                  <Chip tone="info">{t.out.proposals.length} proposal{t.out.proposals.length === 1 ? '' : 's'} — a visitor would confirm these in the widget</Chip>
                )}
                {!compact && !agentToken && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    {(t.out.citations || []).map((c) => (
                      <Chip key={`${c.docId || c.title}-${c.seq || 0}`} tone="info" title={c.docId}>{c.title || c.docId}</Chip>
                    ))}
                    {!(t.out.citations || []).length && <Chip tone="warn" title="Answers come only from published (live) documents — drafts and documents in UAT are not searched.">no citations · only live documents are searched</Chip>}
                    {(t.out.guardrails || []).map((g) => <Chip key={g} tone="ok" title={`guardrail fired: ${g}`}>{GUARDRAIL_SHORT[g] || g}</Chip>)}
                    {t.out.reviewId && <Chip tone="warn">sent to Human Review</Chip>}
                  </div>
                )}
                {onHandoff && (
                  <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => onHandoff(t.q, t.out.text)}>
                    Request human assistance
                  </button>
                )}
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
